// src/pages/Tags/TagManage.jsx
import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Input, Select, Typography, Tooltip, Drawer, Flex, Grid } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import Swal from "sweetalert2";

import {
  createTagFieldApi,
  getTagFieldApi,
  createTagGroupApi,
  getTagGroupNameApi,
  deleteTagFieldApi,
} from "../../../apiservice/setting";
import { getAccessType } from "../../../lib/functions";
import { useIsAdminUser } from "../../../lib/accessList";

const { Option } = Select;

const TagManage = ({ authInfo }) => {
  const [tags, setTags] = useState([]);
  const [groups, setGroups] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [tagFieldName, setTagFieldName] = useState("");
  const [action, setAction] = useState("create");
  const [editId, setEditId] = useState(null);
  let [access_type, access_type_set] = useState("");
  const isAdminUser = useIsAdminUser();
  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;
  const [helpOpen, setHelpOpen] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };
    
  // ✅ fetch data
  const loadData = async () => {
    try {
      const groupRes = await getTagGroupNameApi({
        branch_id: authInfo.userInfo.branch_id,
        institution_id: authInfo.id,
      });
      setGroups(groupRes.data.data || []);

      const fieldRes = await getTagFieldApi();
      setTags(fieldRes.data.data || []);
    } catch (err) {
      Swal.fire("Error", "Failed to load tags", "error");
    }
  };

  useEffect(() => {
    const access = getAccessType("tag_manage");
    access_type_set(access);
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  // ✅ create / update tag group
  const createGroup = async () => {
    if (!groupName.trim()) {
      Swal.fire("Warning", "Tag Group Name is required", "warning");
      return;
    }
    if (groupName.length > 100) {
      Swal.fire("Warning", "Tag Group Name max size exceeded", "warning");
      return;
    }

    const payload = {
      branch_id: authInfo.userInfo.branch_id,
      institution_id: authInfo.id,
      groupname: groupName,
    };

    try {
      const res = await createTagGroupApi(payload);
      Swal.fire(res.data.msg, "", res.data.error ? "warning" : "success");
      if (!res.data.error) {
        setGroupName("");
        loadData();
      }
    } catch (err) {
      Swal.fire("Error", "Failed to create group", "error");
    }
  };

  // ✅ create / update tag field
  const createTag = async () => {
    if (!tagFieldName.trim()) {
      Swal.fire("Warning", "Tag Field Name is required", "warning");
      return;
    }
    if (tagFieldName.length > 100) {
      Swal.fire("Warning", "Tag Field Name max size exceeded", "warning");
      return;
    }

    const payload = {
      tagid: selectedGroupId,
      branch_id: authInfo.userInfo.branch_id,
      field_name: tagFieldName,
      action,
    };
    if (action === "update") payload.id = editId;

    try {
      const res = await createTagFieldApi(payload);
      Swal.fire(
        res.data.msg || "Success",
        "",
        res.data.error ? "warning" : "success"
      );
      if (!res.data.error) {
        resetForm();
        setModalOpen(false);
        loadData();
      }
    } catch (err) {
      Swal.fire("Error", "Failed to save tag", "error");
    }
  };

  // ✅ delete tag field
  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will permanently delete the tag field.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await deleteTagFieldApi({ id });
          Swal.fire(res.data.msg || "Deleted", "", "success");
          loadData();
        } catch (err) {
          Swal.fire("Error", "Failed to delete tag", "error");
        }
      }
    });
  };

  // ✅ edit handler
  const handleEdit = (record) => {
    setSelectedGroupId(record.tag_group_id);
    setTagFieldName(record.field_name);
    setAction("update");
    setEditId(record.id);
    setModalOpen(true);
  };

  const resetForm = () => {
    setGroupName("");
    setSelectedGroupId(null);
    setTagFieldName("");
    setAction("create");
    setEditId(null);
  };

  const columns = [
    { title: "No.", render: (_, __, i) => i + 1 },
    { title: "Tag Group", dataIndex: "group_name" },
    { title: "Tag Field", dataIndex: "field_name" },
    ...(access_type === "WRITE"
      ? [
        {
          title: "Actions",
          render: (_, record) => (
            <>
              <Button
                type="link"
                icon={<EditOutlined />}
                disabled={!(isAdminUser || canEdit)}
                onClick={() => handleEdit(record)}
              />
              <Button
                type="link"
                disabled={!(isAdminUser || canDelete)}
                icon={<DeleteOutlined />}
                danger
                onClick={() => handleDelete(record.id)}
              />
            </>
          ),
        },
      ]
      : []),
  ];

  const { Title } = Typography;
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  return (
    <div>
   <Flex
  justify="space-between"
  align="center"
  wrap="wrap"
  gap={8}
  style={{ marginBottom: 24 }}
>
  <Title level={2} style={{ margin: 0, fontSize: "clamp(20px, 5vw, 30px)" }}>
    Create Tags
  </Title>

  <Flex align="center" gap={6}>
    <Tooltip title="Help">
      <Button
        type="text"
        icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
        onClick={() => setHelpOpen(true)}
      />
    </Tooltip>

    {access_type == "WRITE" && (
          <>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                resetForm();
                setModalOpen(true);
              }}
            >
              Add New
            </Button>
          </>
        )}
  </Flex>
</Flex>
        
        
      <Drawer
        title="Tag Management Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={430}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: 18,
              fontWeight: 600,
              marginBottom: 12,
              marginLeft:12
            }}
          >
            Tag Management
          </h2>

          <p style={{ color: "#555", marginBottom: 16, marginLeft: 17 }}>
            This section helps organize ERP records using custom tags
            and tag groups for better categorization and workflow management.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: 14,
              borderRadius: 8,
              marginBottom: 16,
            }}
          >
            <strong>What You Can Do</strong>

            <ol style={{ paddingLeft: 20, marginTop: 10 }}>
              <li>Create new tag groups</li>
              <li>Create custom tag fields</li>
              <li>Assign tags under different groups</li>
              <li>Edit existing tag names and groups</li>
              <li>Delete unnecessary tags</li>
              <li>Organize records using categorized tags</li>
              <li>Manage ERP workflow classifications</li>
              <li>Maintain structured tagging systems</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: 14,
              borderRadius: 8,
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: 18,
                marginTop: 10,
                color: "#666",
              }}
            >
              <li>Tag groups help organize related tags together.</li>
              <li>Field names should remain short and meaningful.</li>
              <li>Deleted tags may affect linked ERP records.</li>
              <li>Tags improve searching and workflow organization.</li>
              <li>Only authorized users can edit or delete tags.</li>
            </ul>
          </div>
        </div>
      </Drawer>
      <Table
        columns={columns}
        dataSource={tags}
        rowKey="id"
        scroll={{ x: "max-content" }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
        }}
        onChange={handleTableChange}
      />

      <Modal
        open={modalOpen}
        onCancel={() => {
          resetForm();
          setModalOpen(false);
        }}
        onOk={createTag}
        title={action === "create" ? "Add Tag" : "Edit Tag"}
      >
        <div style={{ marginBottom: 12 }}>
          <label>
            Tag Group Name <span style={{ color: "#ff4d4f" }}>*</span>
          </label>
          <Input
            placeholder="New Tag Group"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            style={{ marginBottom: 8 }}
          />
          <Button onClick={createGroup}>+ Create Group</Button>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label>
            Tag Group <span style={{ color: "#ff4d4f" }}>*</span>
          </label>
          <Select
            style={{ width: "100%" }}
            value={selectedGroupId}
            onChange={(val) => setSelectedGroupId(val)}
            placeholder="Select Tag Group"
          >
            {groups.map((g) => (
              <Option key={g.id} value={g.id}>
                {g.group_name}
              </Option>
            ))}
          </Select>
        </div>

        <div>
          <label>
            Tag Field Name<span style={{ color: "#ff4d4f" }}>*</span>
          </label>
          <Input
            placeholder="Tag Field Name"
            value={tagFieldName}
            onChange={(e) => setTagFieldName(e.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
};

export default TagManage;
